import { trickWinner as heartsTrickWinner } from "@bing/engine-hearts";
import { trickWinner as hokmTrickWinner, type HokmTrump } from "@bing/engine-hokm";
import { trickWinner as shelemTrickWinner, type ShelemView } from "@bing/engine-shelem";
import type { TrickPlayLike } from "./use-table-sfx";

/**
 * The rules a finished trick is read under: بی‌دل has no trump; حکم's call is the hand's `trump` —
 * at a hand boundary the CARRIED trick belongs to the hand that just ended, so the table passes
 * `trump ?? lastHand.trump` (the new hand's call is still null). شلم the same way (P1-1): its trump
 * is named by the حاکم's first card, and its 12th trick is carried into the next deal, whose own
 * `trump` is null until that deal's first card — so `trump ?? lastHand.trump` there too.
 */
export type TrickRules =
  | { readonly game: "hearts" }
  | { readonly game: "hokm"; readonly trump: HokmTrump | null | undefined }
  | { readonly game: "shelem"; readonly trump: ShelemView["trump"] | undefined };

/**
 * review 6b F3 — WHO TOOK A FINISHED TRICK, read off its OWN cards with the engines' own pure rules
 * (بی‌دل: the highest card of the led suit; حکم: the highest trump played, else the led suit, the
 * suit-less calls ordering it their way; شلم: the highest trump, else the led suit). The felt used `turn` for it, which is the winner in حکم's
 * frames but NOT in بی‌دل's hand-ending carry (there `turn` is the new deal's). `null` when the
 * trick cannot be read (empty, no حکم call known, a card that does not parse): keep `turn` then.
 */
export function carriedTrickWinner(
  plays: readonly TrickPlayLike[],
  rules: TrickRules,
): number | null {
  if (plays.length === 0) return null;
  try {
    if (rules.game === "hearts") return heartsTrickWinner(plays);
    if (rules.trump === null || rules.trump === undefined) return null;
    // شلم: the highest trump played, else the highest of the led suit — its engine's own rule (it
    // has no suit-less call: a word it does not know is no trump, never حکم's «نرس»)
    if (rules.game === "shelem") return shelemTrickWinner(plays, rules.trump);
    return hokmTrickWinner(plays, rules.trump);
  } catch {
    return null;
  }
}
